import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import type {
  FileFolder,
  FileFolderScope,
  FileRecord,
} from "../entities/files";
import { CloseButton } from "./CloseButton";
import { Select } from "./Select";
import { Notice } from "./Notice";

export interface FolderOption {
  readonly id: string;
  /** Путь папки целиком: «Договоры / 2026», чтобы одинаковые названия не путались. */
  readonly label: string;
}

export interface ScopeOption {
  readonly id: string;
  readonly name: string;
  readonly code: string;
}

const FOCUSABLE =
  'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])';

/**
 * Окно для короткой формы: закрывается по Esc и по фону, держит фокус внутри и
 * возвращает его на кнопку, которая окно открыла.
 */
function DialogFrame({
  title,
  eyebrow,
  onClose,
  children,
}: {
  title: string;
  eyebrow: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = useId();
  const card = useRef<HTMLElement>(null);

  useEffect(() => {
    const opener = document.activeElement;
    const first = card.current?.querySelector<HTMLElement>(
      "input:not([type=checkbox]), select, textarea, input[type=checkbox]",
    );
    (first ?? card.current)?.focus();

    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  function keyboard(event: KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const items = [
      ...(card.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    ];
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="modal" role="presentation">
      <button
        className="modal-backdrop"
        type="button"
        tabIndex={-1}
        aria-label="Закрыть окно"
        onClick={onClose}
      />
      <section
        ref={card}
        className="modal-card folder-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={keyboard}
      >
        <header className="modal-title">
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h2 id={titleId}>{title}</h2>
          </div>
          <CloseButton onClick={onClose} />
        </header>
        {children}
      </section>
    </div>
  );
}

function Actions({
  onClose,
  submit,
  disabled,
  pending,
}: {
  onClose: () => void;
  submit: string;
  disabled: boolean;
  pending: boolean;
}) {
  return (
    <div className="modal-actions folder-dialog__actions">
      <button className="button button--text" type="button" onClick={onClose}>
        Отмена
      </button>
      <button
        className="button button--primary"
        type="submit"
        disabled={disabled || pending}
      >
        {pending ? "Сохраняем…" : submit}
      </button>
    </div>
  );
}

function FormError({ message }: { message: string | null }) {
  return message ? <Notice>{message}</Notice> : null;
}

function editableFilename(filename: string) {
  const separator = filename.lastIndexOf(".");

  return separator > 0 && separator < filename.length - 1
    ? {
        name: filename.slice(0, separator),
        extension: filename.slice(separator),
      }
    : { name: filename, extension: "" };
}

function ParentSelect({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly FolderOption[];
  label: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <Select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">В корне организации</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Select>
    </label>
  );
}

/** Создание папки и переименование: название одно, расположение выбирают только при создании. */
export function FolderNameDialog({
  mode,
  folder,
  parents,
  defaultParentId,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  mode: "create" | "rename";
  folder?: FileFolder;
  parents: readonly FolderOption[];
  defaultParentId: string | null;
  pending: boolean;
  error: string | null;
  onSubmit: (values: { name: string; parentId: string | null }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(folder?.name ?? "");
  const [parentId, setParentId] = useState(defaultParentId ?? "");
  const trimmed = name.trim();
  const unchanged = mode === "rename" && trimmed === folder?.name;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (trimmed === "" || unchanged) return;
    onSubmit({ name: trimmed, parentId: parentId === "" ? null : parentId });
  }

  return (
    <DialogFrame
      eyebrow={mode === "create" ? "Новая папка" : "Папка"}
      title={mode === "create" ? "Создать папку" : "Переименовать папку"}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <label className="field">
          <span>Название</span>
          <input
            value={name}
            maxLength={255}
            required
            autoComplete="off"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        {mode === "create" ? (
          <ParentSelect
            label="Где создать"
            value={parentId}
            onChange={setParentId}
            options={parents}
          />
        ) : null}
        <FormError message={error} />
        <Actions
          onClose={onClose}
          pending={pending}
          disabled={trimmed === "" || unchanged}
          submit={mode === "create" ? "Создать папку" : "Сохранить название"}
        />
      </form>
    </DialogFrame>
  );
}

/** Перенос папки внутрь другой или в корень. */
export function FolderMoveDialog({
  folder,
  parents,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  folder: FileFolder;
  parents: readonly FolderOption[];
  pending: boolean;
  error: string | null;
  onSubmit: (parentId: string | null) => void;
  onClose: () => void;
}) {
  const [parentId, setParentId] = useState(folder.parent_id ?? "");
  const unchanged = parentId === (folder.parent_id ?? "");

  return (
    <DialogFrame
      eyebrow="Папка"
      title={`Перенести «${folder.name}»`}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!unchanged) onSubmit(parentId === "" ? null : parentId);
        }}
      >
        <ParentSelect
          label="Новое расположение"
          value={parentId}
          onChange={setParentId}
          options={parents}
        />
        <p className="muted folder-dialog__note">
          Папку можно перенести только внутри своей организации. Вложенные папки
          и файлы переедут вместе с ней, а доступ КПО, открытый на новую
          родительскую папку, распространится и на эту.
        </p>
        <FormError message={error} />
        <Actions
          onClose={onClose}
          pending={pending}
          disabled={unchanged}
          submit="Перенести"
        />
      </form>
    </DialogFrame>
  );
}

/** Имя файла редактируют отдельно от его содержимого и расположения. */
export function FileRenameDialog({
  filename,
  pending = false,
  error = null,
  onSubmit,
  onClose,
}: {
  filename: string;
  pending?: boolean;
  error?: string | null;
  onSubmit: (filename: string) => void;
  onClose: () => void;
}) {
  const initial = editableFilename(filename);
  const [name, setName] = useState(initial.name);
  const trimmed = name.trim();
  const completed = `${trimmed}${initial.extension}`;
  const extensionChanged =
    editableFilename(completed).extension !== initial.extension;
  const invalidCharacters = [...trimmed].some(
    (character) =>
      character === "/" ||
      character === "\\" ||
      character.charCodeAt(0) < 32 ||
      character.charCodeAt(0) === 127,
  );
  const invalid =
    trimmed === "" ||
    completed.length > 255 ||
    invalidCharacters ||
    extensionChanged;
  const unchanged = completed === filename;

  // Picker бывает внутри формы документа: портал не вкладывает одну форму в другую.
  return createPortal(
    <DialogFrame eyebrow="Файл" title="Переименовать файл" onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!invalid && !unchanged && !pending) onSubmit(completed);
        }}
      >
        <label className="field">
          <span>Название файла</span>
          <input
            value={name}
            maxLength={255 - initial.extension.length}
            required
            autoComplete="off"
            disabled={pending}
            aria-invalid={invalidCharacters || extensionChanged || undefined}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <FormError
          message={
            invalidCharacters
              ? "Название файла не должно содержать /, \\ и управляющие символы."
              : extensionChanged
                ? "Расширение файла изменить нельзя."
                : error
          }
        />
        <Actions
          onClose={onClose}
          pending={pending}
          disabled={invalid || unchanged}
          submit="Сохранить название"
        />
      </form>
    </DialogFrame>,
    document.body,
  );
}

/** Перекладывание файла в папку своей организации или обратно в корень. */
export function FileMoveDialog({
  file,
  folders,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  file: FileRecord;
  folders: readonly FolderOption[];
  pending: boolean;
  error: string | null;
  onSubmit: (folderId: string | null) => void;
  onClose: () => void;
}) {
  // У файла без организации корня нет: он получит организацию папки, поэтому
  // папку надо выбрать явно, а не оставить «как есть».
  const orphan = file.scope_id === null;
  const [folderId, setFolderId] = useState(file.folder_id ?? "");
  const unchanged = folderId === (file.folder_id ?? "");

  return (
    <DialogFrame
      eyebrow="Файл"
      title={`Переместить «${file.original_name ?? file.filename}»`}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!unchanged) onSubmit(folderId === "" ? null : folderId);
        }}
      >
        <label className="field">
          <span>Куда переместить</span>
          <Select
            value={folderId}
            onChange={(event) => setFolderId(event.target.value)}
          >
            <option value="" disabled={orphan}>
              {orphan ? "Выберите папку…" : "В корень (без папки)"}
            </option>
            {folders.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>
        <p className="muted folder-dialog__note">
          {orphan
            ? "У файла нет организации: после переноса он привяжется к организации выбранной папки и станет виден тем КПО, которым эта папка открыта."
            : "Файл можно переложить только в папку его организации. Сам файл при этом не меняется: меняется только то, где он лежит в списке."}
        </p>
        <FormError message={error} />
        <Actions
          onClose={onClose}
          pending={pending}
          disabled={unchanged}
          submit="Переместить"
        />
      </form>
    </DialogFrame>
  );
}

function AccessForm({
  folder,
  ownerName,
  options,
  granted,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  folder: FileFolder;
  ownerName: string | null;
  options: readonly ScopeOption[];
  granted: readonly string[];
  pending: boolean;
  error: string | null;
  onSubmit: (scopeIds: string[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    new Set(granted),
  );
  const [confirming, setConfirming] = useState(false);
  const before = new Set(granted);
  const added = options.filter(
    (option) => selected.has(option.id) && !before.has(option.id),
  );
  const removed = options.filter(
    (option) => !selected.has(option.id) && before.has(option.id),
  );
  const changed = added.length + removed.length > 0;

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  if (confirming) {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(
            options
              .filter((option) => selected.has(option.id))
              .map((option) => option.id),
          );
        }}
      >
        <div
          className="folder-dialog__confirm"
          role="alertdialog"
          aria-label="Подтверждение доступа"
        >
          <p>
            Изменить доступ к папке <strong>«{folder.name}»</strong>?
          </p>
          {added.length > 0 ? (
            <p>
              <strong>Откроется:</strong>{" "}
              {added.map((item) => item.name).join(", ")}. Их сотрудники увидят
              папку, вложенные в неё папки и файлы на чтение.
            </p>
          ) : null}
          {removed.length > 0 ? (
            <p>
              <strong>Закроется:</strong>{" "}
              {removed.map((item) => item.name).join(", ")}. Их сотрудники
              перестанут видеть папку и всё вложенное, если доступ не открыт им
              через другую папку выше.
            </p>
          ) : null}
          {selected.size === 0 ? (
            <p>
              После сохранения папку увидит только{" "}
              {ownerName
                ? `организация «${ownerName}»`
                : "организация-владелец"}
              .
            </p>
          ) : null}
        </div>
        <FormError message={error} />
        <div className="modal-actions folder-dialog__actions">
          <button
            className="button button--text"
            type="button"
            disabled={pending}
            onClick={() => setConfirming(false)}
          >
            Назад
          </button>
          <button
            className="button button--primary"
            type="submit"
            disabled={pending}
          >
            {pending ? "Сохраняем…" : "Подтвердить"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (changed) setConfirming(true);
      }}
    >
      <p className="folder-dialog__note folder-dialog__note--inherit">
        Доступ наследуется вниз: открыв папку другому КПО, вы открываете и все
        папки внутри неё. Обратного правила нет — закрыть вложенную папку,
        оставив родительскую открытой, нельзя. Сотрудники КПО из списка видят
        файлы папки на чтение; удалять и восстанавливать их может только{" "}
        {ownerName ? `организация «${ownerName}»` : "организация-владелец"}.
      </p>
      {options.length === 0 ? (
        <p className="muted">Других КПО для выбора нет.</p>
      ) : (
        <fieldset className="scope-picker">
          <legend>КПО, которым папка показана дополнительно</legend>
          {options.map((option) => (
            <label key={option.id} className="scope-picker__item">
              <input
                type="checkbox"
                checked={selected.has(option.id)}
                onChange={() => toggle(option.id)}
              />
              <span>
                {option.name}
                <small>{option.code}</small>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      <p className="muted folder-dialog__note">
        Без отметок папка видна только организации-владельцу. Набор сохраняется
        целиком.
      </p>
      <FormError message={error} />
      <Actions
        onClose={onClose}
        pending={pending}
        disabled={!changed}
        submit="Сохранить доступ"
      />
    </form>
  );
}

/** Выбор КПО, которым папка открыта. Сохранение — один запрос с подтверждением. */
export function FolderAccessDialog({
  folder,
  ownerName,
  options,
  granted,
  loading,
  loadError,
  pending,
  error,
  onRetry,
  onSubmit,
  onClose,
}: {
  folder: FileFolder;
  ownerName: string | null;
  options: readonly ScopeOption[];
  /** `null`, пока текущий набор не получен: без него нельзя ни показать галочки, ни сравнить. */
  granted: readonly FileFolderScope[] | null;
  loading: boolean;
  /** Почему не получен текущий набор или справочник КПО; `null`, пока всё идёт как надо. */
  loadError: string | null;
  pending: boolean;
  error: string | null;
  onRetry: () => void;
  onSubmit: (scopeIds: string[]) => void;
  onClose: () => void;
}) {
  return (
    <DialogFrame
      eyebrow="Доступ КПО"
      title={`Доступ к «${folder.name}»`}
      onClose={onClose}
    >
      {granted ? (
        <AccessForm
          folder={folder}
          ownerName={ownerName}
          // Уже выданное КПО остаётся в списке, даже если справочник его не
          // вернул: иначе набор сохранился бы без него и доступ пропал молча.
          options={[
            ...options,
            ...granted
              .filter(
                (scope) => !options.some((option) => option.id === scope.id),
              )
              .map((scope) => ({
                id: scope.id,
                name: scope.name,
                code: scope.code,
              })),
          ]}
          granted={granted.map((scope) => scope.id)}
          pending={pending}
          error={error}
          onSubmit={onSubmit}
          onClose={onClose}
        />
      ) : loadError ? (
        <Notice>
          <p>
            Не удалось получить список КПО этой папки: {loadError}. Доступ не
            изменён.
          </p>
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={onRetry}
          >
            Повторить
          </button>
        </Notice>
      ) : (
        <p className="muted" aria-busy={loading}>
          Загружаем доступ…
        </p>
      )}
    </DialogFrame>
  );
}
